// Die Seite „Fragen“ (#/fragen, E3, S6) – für jede Rolle: alle Fragen, die
// der Angemeldete sehen darf (der Verkauf: an seinen Besichtigungen), an
// EINER Stelle. Oben die offenen, wer am längsten wartet zuerst; darunter,
// aufklappbar, die erledigten der letzten 30 Tage.
//
// Je Frage: Kunde · Ort · woran sie hängt („Station 3 · am Foto“, „zur
// Anlage“), der Text, wer, wann, die Antworten – und „Antworten“ und
// „Erledigt“ gleich hier, ohne erst die Besichtigung zu öffnen. „In der
// Besichtigung ansehen“ öffnet sie an der Stelle, wo die Frage hängt
// (sprung.ts) – für Rollen, die die Besichtigung lesen dürfen; wer sie
// nicht liest, sieht nur, woran sie hängt.
//
// Gestellt wird hier nichts: Gefragt wird am Ding selbst (CLAUDE.md,
// Grundsatz 9 – kein allgemeiner Chat). Die offenen Fragen kommen aus dem
// Rahmen (offene.tsx) – dieselbe Liste wie das Band und die Zahl links.
// Keine Erklär-Absätze (Gernot, Augentest 21.10.2026): Jede Karte zeigt
// oben, woran sie hängt; der Kunde ist der Weg dorthin.

import { useMemo, useState } from "react";
import { darf, type FrageMitKontext, type FragenAntwort } from "@happy-energy/kern";
import { Ikon } from "@happy-energy/oberflaeche";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { Seite } from "../rahmen.tsx";
import { useAmServerGeaendert } from "../speicher.ts";
import { useCrm } from "../zusammenhang.tsx";
import { FrageKarte } from "./FragenAmStueck.tsx";
import { aeltesteZuerst, anzahl, ERLEDIGT_TAGE, erledigteDerLetztenZeit, ortText } from "./fragen.ts";
import { useOffeneFragen } from "./offene.tsx";
import { frageMerken } from "./sprung.ts";

/** Kunde · Ort · woran – dazu der Weg in die Besichtigung, an die Stelle der Frage, wenn die Rolle sie lesen darf. */
function Zusammenhang({ f, mitWeg }: { f: FrageMitKontext; mitWeg: boolean }) {
  return (
    <>
      <span>
        <b>{f.kontext.kundeName}</b>
        {` · ${f.kontext.ort} · ${ortText(f.bezug.tabelle, f.kontext.stationNr)}`}
      </span>
      {mitWeg && (
        <a href={`#/freigabe/${f.aufnahmeId}`} onClick={() => frageMerken(f.aufnahmeId, f.id)} title="Die Besichtigung an dieser Stelle öffnen">
          in der Besichtigung ansehen <Ikon name="rechts" groesse="k" />
        </a>
      )}
    </>
  );
}

export function FragenSeite() {
  const { ich } = useCrm();
  const mitWeg = darf(ich.nutzer.rolle, "aufnahme-lesen");
  const { fragen, fehler, neu } = useOffeneFragen();
  const offen = useMemo(() => (fragen ? aeltesteZuerst(fragen.filter((f) => f.stand === "offen")) : null), [fragen]);

  // Die erledigten erst, wenn jemand sie sehen will – sie werden mit der Zeit viele.
  const [erledigteAuf, setErledigteAuf] = useState(false);
  const alle = useLaden<FragenAntwort>(erledigteAuf ? "/api/crm/fragen" : null);
  useAmServerGeaendert(alle.neu);
  const erledigte = useMemo(() => (alle.daten ? erledigteDerLetztenZeit(alle.daten.fragen, new Date()) : null), [alle.daten]);

  const neuLaden = () => {
    neu();
    alle.neu();
  };

  return (
    <Seite titel="Fragen" unter="Offene oben – wer am längsten wartet, steht zuerst" ohneFragenBand>
      {offen === null && fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {offen === null && fehler === undefined && <Laedt text="Die Fragen werden geladen …" />}
      {offen && (
        <section className="fragen-liste" aria-label="Offene Fragen">
          <div className="crm-abschnitt">
            <h2>{offen.length === 0 ? "Keine offenen Fragen" : anzahl(offen.length, "1 offene Frage", "# offene Fragen")}</h2>
          </div>
          {offen.length === 0 ? (
            <p className="fragen-leer">
              <Ikon name="haken" groesse="k" /> Alles beantwortet und erledigt.
            </p>
          ) : (
            offen.map((f) => <FrageKarte key={f.id} frage={f} neuLaden={neuLaden} zusammenhang={<Zusammenhang f={f} mitWeg={mitWeg} />} />)
          )}
        </section>
      )}

      <section className="fragen-liste" aria-label="Erledigte Fragen">
        <div>
          <button type="button" className="crm-knopf rand klein" aria-expanded={erledigteAuf} onClick={() => setErledigteAuf((x) => !x)}>
            <Ikon name="haken" groesse="k" />
            {erledigteAuf ? "Erledigte ausblenden" : `Erledigte der letzten ${ERLEDIGT_TAGE} Tage zeigen`}
          </button>
        </div>
        {erledigteAuf && alle.fehler !== undefined && <LadeFehler fehler={alle.fehler} nochmal={alle.neu} />}
        {erledigteAuf && erledigte === null && alle.fehler === undefined && <Laedt />}
        {erledigteAuf && erledigte?.length === 0 && <p className="leise klein fragen-hinweis">Keine erledigt in den letzten {ERLEDIGT_TAGE} Tagen.</p>}
        {erledigteAuf &&
          erledigte?.map((f) => <FrageKarte key={f.id} frage={f} neuLaden={neuLaden} zusammenhang={<Zusammenhang f={f} mitWeg={mitWeg} />} />)}
      </section>
    </Seite>
  );
}
